import { apiClient } from '@api/apiClient';
import { V4ApiResponse } from '@api/types';
import { ToastType } from '@components/Toast/Toast';
import { FeatureFlags } from '@constants/featureFlags';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import {
  availableServices,
  EquipmentTypeCodeBoard,
  EquipmentTypeCodeRequest,
} from '@typeDefinitions/postingTypes';
import type { ChangeTrackingSource } from '@typeDefinitions/changeTrackingTypes';
import { logError } from '@utils/errorLogging';
import { createError } from '@utils/errorUtils';
import { handleToast } from '@utils/utils';
import { CHANGE_TRACKING_SOURCES } from '@changeTracking/utils/changeTrackingRegistry';
import { buildChangeTrackingRequestConfig } from '@changeTracking/utils/buildChangeTrackingHeaders';
import {
  FetchPricingForPostingPayload,
  FetchPricingForPostingResponse,
  IManagePostLoad,
  IPostLoad,
} from '@store/orders/types';

import { buildSourceTrackingConfig } from './serviceHelpers';

export async function deletePosting(
  postingId: number | null,
  changeTrackingSource?: ChangeTrackingSource,
) {
  try {
    const response: V4ApiResponse<boolean> = await apiClient.delete(
      `/posting/${postingId}`,
      buildSourceTrackingConfig(changeTrackingSource),
    );
    if (response.isSuccess === false) {
      throw response.errors;
    }
    if (Boolean(response)) {
      handleToast(ToastType.Success, 'Posting deleted successfully');
    }
    return response;
  } catch (error: unknown) {
    logError('deletePosting', error as Error);
    throw createError(error, 'Failed to delete posting');
  }
}

export async function forceDeletePosting(
  postingId: number | null,
  changeTrackingSource?: ChangeTrackingSource,
) {
  try {
    const response: V4ApiResponse<boolean> = await apiClient.delete(
      `/posting/force/${postingId}`,
      buildSourceTrackingConfig(changeTrackingSource),
    );
    if (response.isSuccess === false) {
      throw response.errors;
    }
    if (Boolean(response)) {
      handleToast(ToastType.Success, 'Posting deleted successfully');
    }
    return response;
  } catch (error: unknown) {
    logError('forceDeletePosting', error as Error);
    throw createError(error, 'Failed to force delete posting');
  }
}

export async function postPosting(
  loadId: number,
  loadNumber?: string,
): Promise<number | undefined> {
  try {
    const shouldSendLoadNumberInComment = isFeatureFlagEnabled(
      FeatureFlags.feature_tms_Prepopulate_LoadNumber_Single_Posting_Comment,
    )();
    const payload = {
      loadId,
      ...(shouldSendLoadNumberInComment && loadNumber
        ? { comment: loadNumber }
        : {}),
    };
    const response: {
      data: { id: number };
      errors: [];
      partialSuccess: boolean;
    } = await apiClient.post(
      '/posting/quickpost',
      payload,
      buildChangeTrackingRequestConfig([
        CHANGE_TRACKING_SOURCES.orderDetails.loadDetailsPostLoad,
      ]),
    );
    const postingId = response.data.id;
    if (response.partialSuccess) {
      const errors = response.errors.map((error: string) => {
        if (error.includes('TruckStop') && error.includes('password')) {
          return 'TruckStop Password Is Invalid';
        }
        return error;
      });
      handleToast(
        ToastType.Caution,
        `Posting Load Partial Success: ${errors.join(', ')}`,
      );
    } else {
      handleToast(ToastType.Success, 'Posting Load Success');
    }
    return postingId;
  } catch (error: unknown) {
    logError('postPosting', error as Error);
    throw createError(error, 'Failed to post load');
  }
}

export async function fetchAvailableServices(): Promise<availableServices[]> {
  try {
    const response = await apiClient.get('posting/getavailableservices');

    if ('value' in response) {
      return response.value as availableServices[];
    }
    throw new Error('Failed to fetch avilable services');
  } catch (error: unknown) {
    logError('fetchAvailableServices', error as Error);
    throw createError(error, 'Failed to fetch available services');
  }
}

export async function fetchEquipmentTypeCodes(
  payload: EquipmentTypeCodeRequest,
): Promise<EquipmentTypeCodeBoard[]> {
  try {
    const response = await apiClient.post('posting/equipmenttypecode', payload);

    if (Array.isArray(response)) {
      return response as EquipmentTypeCodeBoard[];
    }
    if ('value' in response) {
      return response.value as EquipmentTypeCodeBoard[];
    }
    return [];
  } catch (error: unknown) {
    logError('fetchEquipmentTypeCodes', error as Error);
    throw createError(error, 'Failed to fetch equipment type codes');
  }
}

const handlePostingError = (
  error: unknown,
  toastMessage: string,
  toastOnLeft = false,
) => {
  if (Array.isArray(error)) {
    const errorMessages = error.map((v) => {
      if (typeof v === 'string') {
        return v;
      } else if (typeof v === 'object' && v !== null && 'errorMessage' in v) {
        return (v as { errorMessage: string }).errorMessage;
      }
    });

    handleToast(
      ToastType.Error,
      errorMessages.join(', '),
      undefined,
      toastOnLeft ? 'top-left' : 'top-right',
    );
  } else {
    handleToast(
      ToastType.Error,
      toastMessage,
      undefined,
      toastOnLeft ? 'top-left' : 'top-right',
    );
  }
};

export async function createPostLoad(
  value: IPostLoad,
  toastOnLeft = false,
  changeTrackingSource?: ChangeTrackingSource,
) {
  try {
    const postLoadResponse: {
      data: { id: number };
      errors: [];
      partialSuccess: boolean;
    } = await apiClient.post(
      '/Posting',
      value,
      buildSourceTrackingConfig(changeTrackingSource),
    );

    if (postLoadResponse.partialSuccess) {
      const errors = postLoadResponse.errors.map((error: string) => {
        if (error.includes('TruckStop') && error.includes('password')) {
          return 'TruckStop Password Is Invalid';
        }
        return error;
      });

      handleToast(
        ToastType.Caution,
        `Posting Load Partial Success: ${errors.join('\n')}`,
        undefined,
        toastOnLeft ? 'top-left' : 'top-right',
        5000,
      );
    } else {
      handleToast(
        ToastType.Success,
        'Posting Load Success',
        undefined,
        toastOnLeft ? 'top-left' : 'top-right',
        5000,
      );
    }
    return postLoadResponse.data as unknown as IPostLoad;
  } catch (error: unknown) {
    handlePostingError(error, 'Failed to make create post load', toastOnLeft);
  }
}

export async function updatePostLoad(
  value: Partial<IManagePostLoad>,
  toastOnLeft = false,
  changeTrackingSource?: ChangeTrackingSource,
): Promise<IManagePostLoad> {
  try {
    const postLoadResponse: IManagePostLoad = await apiClient.put(
      '/Posting',
      value,
      buildSourceTrackingConfig(changeTrackingSource),
    );
    const errors = postLoadResponse.errors;

    if (errors.length > 0) {
      handleToast(
        ToastType.Error,
        `${errors.join('\n')}`,
        undefined,
        toastOnLeft ? 'top-left' : 'top-right',
      );
    } else {
      handleToast(
        ToastType.Success,
        'Posted!',
        undefined,
        toastOnLeft ? 'top-left' : 'top-right',
        5000,
      );
    }

    return postLoadResponse as unknown as IManagePostLoad;
  } catch (error: unknown) {
    logError('updatePostLoad', error as Error);
    throw createError(error, 'Failed to make update post load');
  }
}

export async function fetchPosting(postId: number): Promise<IManagePostLoad> {
  try {
    const response = await apiClient.get(`posting/${postId}`);

    return response as unknown as IManagePostLoad;
  } catch (error: unknown) {
    logError('fetchPosting', error as Error);
    throw createError(error, 'Failed to fetch posting');
  }
}

export async function fetchPricingForPosting(
  payload: FetchPricingForPostingPayload,
) {
  try {
    const response: V4ApiResponse<FetchPricingForPostingResponse> =
      await apiClient.post('/Pricing', payload);
    if ('value' in response) {
      return response.value;
    }
  } catch (error: unknown) {
    const e = createError(error, 'Failed to fetch pricing for posting');
    handleToast(ToastType.Error, e.message);
  }
}
